import { useQuery } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { Sparkles } from 'lucide-react';
import { getGBrainSkills } from '@/lib/api';

/**
 * SkillsPanel
 *
 * Discovered G-Skills list; capped at 8, shows "+ N more" if truncated
 */
export function SkillsPanel(): React.ReactElement | null {
  const { selectedProjectId } = useProject();

  const { data: skillsData } = useQuery({
    queryKey: ['g-brain-skills', selectedProjectId],
    queryFn: () => {
      if (!selectedProjectId) throw new Error('No project selected');
      return getGBrainSkills(selectedProjectId);
    },
    enabled: !!selectedProjectId,
    staleTime: 30_000,
  });

  const skills = skillsData?.skills ?? [];
  const displayed = skills.slice(0, 8);
  const hidden = Math.max(0, skills.length - 8);

  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        G-Skills
      </div>

      {skills.length > 0 ? (
        <div className="flex-1 space-y-1 overflow-y-auto">
          {displayed.map((skill, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <Sparkles className="h-3 w-3 flex-shrink-0 text-primary" />
              <span className="truncate text-text-primary">{skill}</span>
            </div>
          ))}
          {hidden > 0 && (
            <div className="text-[10px] text-text-tertiary pt-1 border-t border-border/50">
              + {hidden} more
            </div>
          )}
        </div>
      ) : (
        <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
          No G-Skills discovered yet
        </div>
      )}
    </div>
  );
}
